import { useEffect, useRef, useCallback } from 'react';
import { useSubscription, useApolloClient } from '@apollo/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import {
	ON_ALERT_CREATED_SUBSCRIPTION,
	type AlertCreatedPayload
} from '@/components/alerts/alerts.gql';
import { createWsClient, disposeWsClient } from '@/lib/apolloClient';

interface UseAlertSubscriptionOptions {
	/** Whether to show toast notifications for new alerts */
	showToast?: boolean;
	/** Whether to auto-refresh alerts list on new alert */
	refreshAlerts?: boolean;
}

/**
 * Hook to subscribe to real-time maintenance events (alerts).
 * Automatically manages subscription lifecycle based on authentication state.
 * Shows toast notifications and refreshes maintenance snapshot on new events.
 */
export function useAlertSubscription(
	options: UseAlertSubscriptionOptions = {}
) {
	const { showToast = true, refreshAlerts = true } = options;
	const { t } = useTranslation(['alerts', 'common']);
	const { isAuthenticated, isLoading: authLoading } = useAuth();
	const apolloClient = useApolloClient();
	const isSubscribed = useRef(false);
	const prevAuthState = useRef<boolean | null>(null);

	// Handle WebSocket connection lifecycle based on auth state
	useEffect(() => {
		// Skip if auth is still loading
		if (authLoading) return;

		// Only act on auth state changes
		if (prevAuthState.current === isAuthenticated) return;
		prevAuthState.current = isAuthenticated;

		if (isAuthenticated) {
			// User logged in - create WebSocket connection
			console.log('[AlertSubscription] User authenticated, connecting...');
			createWsClient();
			isSubscribed.current = true;
		} else {
			// User logged out - dispose WebSocket connection
			console.log('[AlertSubscription] User logged out, disconnecting...');
			disposeWsClient();
			isSubscribed.current = false;
		}

		return () => {
			// Cleanup on unmount
			if (isSubscribed.current) {
				disposeWsClient();
				isSubscribed.current = false;
			}
		};
	}, [isAuthenticated, authLoading]);

	// Callback to refresh alerts in the cache
	const refreshAlertsCache = useCallback(() => {
		if (!refreshAlerts) return;

		// Refetch alerts queries to update the list
		apolloClient.refetchQueries({
			include: ['Alerts', 'AlertsList']
		});
	}, [apolloClient, refreshAlerts]);

	// Handle new alert received
	const handleAlertReceived = useCallback(
		(event: AlertCreatedPayload) => {
			console.log('[AlertSubscription] New alert received:', event);

			// Refresh the alerts list
			refreshAlertsCache();

			// Show toast notification
			if (showToast) {
				const title = event.title || t(`alerts:severity.${event.severity.toLowerCase()}`, event.severity);
				const message = event.message || t('alerts:newAlertReceived');

				// Use appropriate toast type based on severity
				if (event.severity === 'CRITICAL') {
					toast.error(title, {
						description: message,
						position: 'top-right',
						duration: 10000
					});
				} else if (event.severity === 'ERROR') {
					toast.error(title, {
						description: message,
						position: 'top-right',
						duration: 8000
					});
				} else if (event.severity === 'WARNING') {
					toast.warning(title, {
						description: message,
						position: 'top-right',
						duration: 5000
					});
				} else {
					toast.info(title, {
						description: message,
						position: 'top-right',
						duration: 4000
					});
				}
			}
		},
		[refreshAlertsCache, showToast, t]
	);

	// Subscribe to alert created events - only when authenticated
	const { data, loading, error } = useSubscription<{
		onAlertCreated: AlertCreatedPayload;
	}>(ON_ALERT_CREATED_SUBSCRIPTION, {
		skip: !isAuthenticated || authLoading,
		onData: ({ data: subscriptionData }) => {
			if (subscriptionData?.data?.onAlertCreated) {
				handleAlertReceived(subscriptionData.data.onAlertCreated);
			}
		},
		onError: (subscriptionError) => {
			console.error(
				'[AlertSubscription] Subscription error:',
				subscriptionError
			);
		}
	});

	// Get the latest alert from subscription data
	const latestAlert: AlertCreatedPayload | null = data?.onAlertCreated ?? null;

	return {
		/** The latest alert received from subscription */
		latestAlert,
		/** Whether the subscription is currently loading */
		isLoading: loading,
		/** Any error from the subscription */
		error,
		/** Whether currently subscribed (authenticated and connected) */
		isSubscribed: isAuthenticated && !authLoading && isSubscribed.current,
		/** Manually trigger alerts refresh */
		refreshAlerts: refreshAlertsCache
	};
}
